/**
 * Utility functions for subscription plan redirection to Order Center (OC)
 * Handles both first-time purchase and plan change scenarios
 * Plan IDs are fetched from the API response
 */

import Cookies from 'universal-cookie';
import { API_ENDPOINTS } from '@/api/apiConfig';
import axiosInstance from '@/api/axiosInstance';
import { MSP_MANAGE_SUBSCRIPTIONS_PATH } from './subscriptionPaths';
const cookies = new Cookies();

// Action types for OC redirect
export const OC_ACTION = {
  FIRST_TIME_PURCHASE: '1',
  BUY_MORE: '2',  // Add more credits to current plan (Buy More)
  CHANGE_PLAN: '5',  // Change/upgrade subscription plan
  EDIT_BILLING: '6'  // Edit billing information
};

/**
 * Get the billing URL based on environment and action type
 * @param {string} action - Action type: '1' for first-time purchase (payment-portal), '2' for buy more (checkout)
 * @returns {string} Billing URL from environment config
 */
const getBillingUrl = (action) => {
  // Get base billing URL from environment
  const baseBillingUrl = process.env.billingUrl || 'https://billing-stg.netgear.com/payment-portal';
  
  // For action=1 (first-time purchase), use /payment-portal
  // For action=2 (buy more), use /checkout
  if (action === OC_ACTION.BUY_MORE) {
    // Replace /payment-portal with /checkout for Buy More action
    return baseBillingUrl.replace('/payment-portal', '/checkout');
  }
  
  // Default to payment-portal for first-time purchase and edit billing
  return baseBillingUrl;
};

/**
 * Get the continue/redirect URL based on environment
 * @returns {string} Continue URL for redirect after payment
 */
const getContinueUrl = () => {
  // Get base URL from environment (e.g., https://pri-qa.insight.netgear.com)
  let baseUrl = process.env.camSdkRedirectUrl | window.location.origin;
  
  // Remove trailing slash if present
  baseUrl = baseUrl.replace(/\/$/, '');
  
  // Build the continue URL with encoded hash route
  // Format: https://pri-qa.insight.netgear.com/%23/accountManagement/subScriptions
  const subscriptionPath = '%23/accountManagement/subScriptions';
  let continueUrl = `${baseUrl}/${subscriptionPath}`;
  
  // Add paymentRedirectParam from cookies if available
  const paymentRedirectParam = cookies.get('paymentRedirectParam');
  if (paymentRedirectParam) {
    continueUrl += `?${paymentRedirectParam}`;
  }
  
  return continueUrl;
};

/**
 * Build the OC redirect URL for subscription plans
 * @param {Object} options - Configuration options
 * @param {Object} options.planData - Plan data object from API containing planId (required)
 * @param {string} options.planData.planId - Plan ID from API (e.g., "288338", "288330", etc.)
 * @param {string} options.action - Action type: '1' for first-time purchase, '2' for buy more credits (add credits to current plan), '5' for change plan
 * @param {number} [options.deviceCredits=2] - Number of device credits
 * @param {number} [options.newDeviceCredits=1] - Number of new device credits
 * @param {string} [options.tokenType='v2'] - Token type
 * @param {string} [options.customContinueUrl] - Custom continue URL (optional)
 * @param {string} [options.oldPlanId] - Old plan ID (required for action=5 change plan)
 * @returns {string} Complete OC redirect URL
 */
export const buildSubscriptionRedirectUrl = ({
  planData,
  action = OC_ACTION.FIRST_TIME_PURCHASE,
  deviceCredits = 2,
  newDeviceCredits = 1,
  tokenType = 'v2',
  customContinueUrl = null,
  oldPlanId = null
}) => {
  // Validate planData and planId
  if (!planData || !planData.planId) {
    console.error('Invalid planData: planData.planId is required');
    return null;
  }

  // For change plan (action=5), oldPlanId is required
  if (action === OC_ACTION.CHANGE_PLAN && !oldPlanId) {
    console.error('oldPlanId is required for change plan action');
    return null;
  }

  // Get planId from API response
  const planId = planData.planId;

  // Get billing URL based on action type
  const billingUrl = getBillingUrl(action);

  // Get continue URL
  const continueUrl = customContinueUrl || getContinueUrl();

  // Build query parameters in the correct order
  // Order: action, planId, oldPlanId (if change plan), deviceCredits, newDeviceCredits, tokenType, continue
  const params = new URLSearchParams();
  params.append('action', action);
  params.append('planId', planId);
  
  // Add oldPlanId right after planId for change plan action (action=5)
  if (action === OC_ACTION.CHANGE_PLAN && oldPlanId) {
    params.append('oldPlanId', oldPlanId);
  }
  
  params.append('deviceCredits', deviceCredits.toString());
  params.append('newDeviceCredits', newDeviceCredits.toString());
  params.append('tokenType', tokenType);
  params.append('continue', continueUrl);

  // Build complete URL
  const redirectUrl = `${billingUrl}?${params.toString()}`;

  console.log('Building subscription redirect URL:', {
    planId,
    oldPlanId: action === OC_ACTION.CHANGE_PLAN ? oldPlanId : undefined,
    planName: planData.planName,
    frequency: planData.frequency,
    action,
    redirectUrl
  });

  return redirectUrl;
};

/**
 * Build the OC redirect URL for editing billing information
 * @param {Object} options - Configuration options
 * @param {Object} options.planData - Plan data object from API containing planId (required)
 * @param {string} options.planData.planId - Plan ID from API (e.g., "288338", "288330", etc.)
 * @param {string} [options.tokenType='v2'] - Token type
 * @param {string} [options.customContinueUrl] - Custom continue URL (optional)
 * @returns {string} Complete OC redirect URL for edit billing
 */
export const buildEditBillingRedirectUrl = ({
  planData,
  tokenType = 'v2',
  customContinueUrl = null
}) => {
  // Validate planData and planId
  if (!planData || !planData.planId) {
    console.error('Invalid planData: planData.planId is required for edit billing');
    return null;
  }

  // Get planId from API response
  const planId = planData.planId;

  // Get billing URL (edit billing uses payment-portal)
  const billingUrl = getBillingUrl(OC_ACTION.EDIT_BILLING);

  // Get continue URL
  const continueUrl = customContinueUrl || getContinueUrl();

  // Build query parameters for edit billing (action=6, no deviceCredits needed)
  const params = new URLSearchParams({
    action: OC_ACTION.EDIT_BILLING,
    planId: planId,
    tokenType: tokenType,
    continue: continueUrl
  });

  // Build complete URL
  const redirectUrl = `${billingUrl}?${params.toString()}`;

  console.log('Building edit billing redirect URL:', {
    planId,
    planName: planData.planName,
    redirectUrl
  });

  return redirectUrl;
};

/**
 * Redirect to OC payment portal for subscription purchase/change
 * @param {Object} options - Configuration options (same as buildSubscriptionRedirectUrl)
 */
export const redirectToSubscriptionPayment = (options) => {
  const redirectUrl = buildSubscriptionRedirectUrl(options);
  
  if (redirectUrl) {
    console.log('Redirecting to OC payment portal:', redirectUrl);
    // Replace so Insight page is not stacked under external payment; reduces Back → Stripe after logout.
    window.location.replace(redirectUrl);
  } else {
    console.error('Failed to build redirect URL. Cannot redirect.');
  }
};

/** Base product key for user/v2/checkout when billing is opened during grace (not a specific term price id). */
export const STRIPE_CHECKOUT_INSIGHT_PLAN_KEY = 'Insight';

/** Stripe success/cancel URLs must stay on the MSP-level route; org-scoped URLs break checkout allowlists. */
const STRIPE_MANAGE_SUBSCRIPTIONS_PATH = MSP_MANAGE_SUBSCRIPTIONS_PATH;

export const createStripeCheckoutSession = async (planKey, quantity, options = {}) => {
  try {
    const _Id = cookies.get('_Id');
    if (!_Id) {
      throw new Error('Missing user ID');
    }

    const endpointUrl = API_ENDPOINTS.CHECKOUT_SUBSCRIPTION.url.replace('{_Id}', _Id);

    // Append stripe_success=1 so the app can detect the post-payment return and
    // trigger a data refresh without relying on a manual reload.
    // Always use MSP-level manage-subscriptions (not /organization/:org/...) so payloads match
    // the all-organizations navigation flow and backend/Stripe redirect validation.
    // Prepend the router basename (VITE_APP_BASE_NAME) so deployments hosted under a
    // subpath (e.g. /react/stage) produce valid Stripe success/cancel URLs.
    const rawBasename = import.meta.env.VITE_APP_BASE_NAME || '';
    const routerBasename = rawBasename === '/' ? '' : rawBasename;
    const baseHref = `${window.location.origin}${routerBasename}${STRIPE_MANAGE_SUBSCRIPTIONS_PATH}`;
    const successFlag = options.scheduleDecrease ? 'qty_sched' : options.qtyChange ? 'qty' : '1';
    const successUrl = options.portalReturn ? baseHref : `${baseHref}?stripe_success=${successFlag}`;
    const cancelUrl = baseHref;

    const checkoutRequest = { planKey, successUrl, cancelUrl };
    if (quantity != null) checkoutRequest.quantity = quantity;

    const response = await axiosInstance({
      method: API_ENDPOINTS.CHECKOUT_SUBSCRIPTION.method,
      url: endpointUrl,
      data: checkoutRequest,
      headers: {
        token: localStorage.getItem('token'),
        platform: 'WEB',
        currentVer: '',
        accountId: cookies.get('accountId'),
        apiKey: process.env.apiKey
      }
    });

    const checkoutUrl = response?.data?.details;
    console.log('response', checkoutUrl);

    if (checkoutUrl) {
      // Replace current Insight URL so Back from Stripe does not land on the same pre-checkout page twice
      // and logout + Back is less likely to surface Stripe again.
      window.location.replace(checkoutUrl);
    } else {
      console.error('Checkout URL missing from response');
    }
  } catch (error) {
    console.error('Failed to create checkout session:', error);
  }
};

/**
 * Same Stripe checkout as Choose Plan when the user selects the Monthly card:
 * `plans.monthly` from subscription plans API — gate on `planId`, send `planType` as checkout `planKey`.
 *
 * @param {{ planId?: string, planType?: string }|null|undefined} monthlyPlanRow - typically `plans.monthly`
 */
export const startChoosePlanMonthlyStripeCheckout = (monthlyPlanRow) => {
  if (!monthlyPlanRow?.planId) {
    return Promise.resolve();
  }
  // Same fallback as grace Buy More: backend checkout may need base Insight key if planType is absent.
  const planKey = monthlyPlanRow.planType || STRIPE_CHECKOUT_INSIGHT_PLAN_KEY;
  return createStripeCheckoutSession(planKey, 1);
};

/**
 * Redirect to OC payment portal for editing billing information
 * @param {Object} options - Configuration options (same as buildEditBillingRedirectUrl)
 */
export const redirectToEditBilling = (options) => {
  const redirectUrl = buildEditBillingRedirectUrl(options);
  
  if (redirectUrl) {
    console.log('Redirecting to edit billing:', redirectUrl);
    window.location.replace(redirectUrl);
  } else {
    console.error('Failed to build edit billing redirect URL. Cannot redirect.');
  }
};

export default {
  buildSubscriptionRedirectUrl,
  buildEditBillingRedirectUrl,
  redirectToSubscriptionPayment,
  redirectToEditBilling,
  OC_ACTION
};
